import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { FileUp, CalendarClock } from "lucide-react";
import { fmtDate } from "@/lib/format";

const today = () => new Date().toISOString().slice(0, 10);
const addDays = (iso, n) => {
    const d = new Date(iso + "T00:00:00Z");
    d.setUTCDate(d.getUTCDate() + n);
    return d.toISOString().slice(0, 10);
};

/** Upload the customer's invoice PDF from the old system and schedule the
 *  next auto-invoice 90 days after that invoice's date. */
export function LegacyInvoiceDialog({ open, onOpenChange, customer, onCreated }) {
    const [form, setForm] = useState(null);
    const [nextTouched, setNextTouched] = useState(false);
    const [busy, setBusy] = useState(false);
    const fileRef = useRef(null);

    useEffect(() => {
        if (!open || !customer) return;
        const inv = today();
        setForm({
            amount: customer.monthly_rate || "", invoice_date: inv,
            due_date: addDays(inv, 14), legacy_invoice_number: "",
            status: "open", next_billing_date: addDays(inv, 90),
        });
        setNextTouched(false);
    }, [open, customer]);

    if (!customer || !form) return null;
    const set = (k, v) => setForm((f) => {
        const next = { ...f, [k]: v };
        if (k === "invoice_date" && v && !nextTouched) next.next_billing_date = addDays(v, 90);
        return next;
    });

    const submit = async () => {
        const file = fileRef.current?.files?.[0];
        if (!file) return toast.error("Attach the invoice PDF");
        if (!(parseFloat(form.amount) > 0)) return toast.error("Enter the invoice amount");
        if (!form.invoice_date || !form.due_date) return toast.error("Invoice and due dates are required");
        setBusy(true);
        try {
            const fd = new FormData();
            fd.append("file", file);
            Object.entries(form).forEach(([k, v]) => fd.append(k, v ?? ""));
            const { data } = await api.post(`/customers/${customer.id}/legacy-invoice`, fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`Legacy invoice saved — next invoice auto-sends ${fmtDate(data.next_billing_date)}`);
            onCreated?.(data);
            onOpenChange(false);
        } catch (e) {
            toast.error(e.response?.data?.detail || "Upload failed");
        } finally { setBusy(false); }
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-lg" data-testid="legacy-invoice-dialog">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <FileUp className="w-4 h-4" />Upload legacy invoice
                    </DialogTitle>
                    <DialogDescription>
                        {customer.name} · {customer.account_number}. Attach the invoice PDF from the
                        old system. Waste HQ will auto-generate and send the next one 90 days later.
                    </DialogDescription>
                </DialogHeader>
                <div className="space-y-4 mt-2">
                    <div>
                        <Label className="text-xs">Invoice PDF</Label>
                        <input ref={fileRef} type="file" accept="application/pdf,.pdf"
                               className="block w-full text-sm border border-input rounded p-2 bg-background mt-1"
                               data-testid="legacy-invoice-file" />
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                        <LField label="Amount ($)">
                            <Input type="number" step="0.01" min="0" value={form.amount}
                                   onChange={(e) => set("amount", e.target.value)}
                                   data-testid="legacy-invoice-amount" />
                        </LField>
                        <LField label="Old invoice # (optional)">
                            <Input value={form.legacy_invoice_number}
                                   onChange={(e) => set("legacy_invoice_number", e.target.value)}
                                   data-testid="legacy-invoice-number" />
                        </LField>
                        <LField label="Invoice date">
                            <Input type="date" value={form.invoice_date}
                                   onChange={(e) => set("invoice_date", e.target.value)}
                                   data-testid="legacy-invoice-date" />
                        </LField>
                        <LField label="Due date">
                            <Input type="date" value={form.due_date}
                                   onChange={(e) => set("due_date", e.target.value)}
                                   data-testid="legacy-invoice-due-date" />
                        </LField>
                        <LField label="Status">
                            <Select value={form.status} onValueChange={(v) => set("status", v)}>
                                <SelectTrigger data-testid="legacy-invoice-status"><SelectValue /></SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="open">Open — customer still owes</SelectItem>
                                    <SelectItem value="paid">Already paid</SelectItem>
                                </SelectContent>
                            </Select>
                        </LField>
                        <LField label="Next auto-invoice">
                            <Input type="date" value={form.next_billing_date}
                                   onChange={(e) => { setNextTouched(true); set("next_billing_date", e.target.value); }}
                                   data-testid="legacy-invoice-next-billing" />
                        </LField>
                    </div>
                    <div className="flex items-start gap-2 text-xs text-muted-foreground bg-secondary/40 rounded p-2.5">
                        <CalendarClock className="w-4 h-4 shrink-0 mt-0.5" />
                        <span data-testid="legacy-invoice-next-hint">
                            On {fmtDate(form.next_billing_date)} a new invoice for this customer's
                            rate is generated and sent automatically (after the 24h review hold).
                        </span>
                    </div>
                    <div className="flex justify-end gap-2 pt-2 border-t border-border">
                        <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
                        <Button onClick={submit} disabled={busy} data-testid="legacy-invoice-submit">
                            {busy ? "Uploading..." : "Save invoice"}
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

function LField({ label, children }) {
    return (
        <div className="space-y-1">
            <Label className="text-xs">{label}</Label>
            {children}
        </div>
    );
}
